import { ThemeOptions } from "@mui/material/styles/createTheme";

import borders from "@/themes/base/borders";
import boxShadows from "@/themes/base/boxShadows";
import breakpoints from "@/themes/base/breakpoints";
import colors from "@/themes/base/colors";
import globals from "@/themes/base/globals";
import typography from "@/themes/base/typography";
import appBar from "@/themes/components/appBar";
import avatar from "@/themes/components/avatar";
import breadcrumbs from "@/themes/components/breadcrumbs";
import button from "@/themes/components/button";
import buttonBase from "@/themes/components/buttonBase";
import card from "@/themes/components/card";
import cardContent from "@/themes/components/card/cardContent";
import cardMedia from "@/themes/components/card/cardMedia";
import container from "@/themes/components/container";
import dialog from "@/themes/components/dialog";
import dialogActions from "@/themes/components/dialog/dialogActions";
import dialogContent from "@/themes/components/dialog/dialogContent";
import dialogContentText from "@/themes/components/dialog/dialogContentText";
import dialogTitle from "@/themes/components/dialog/dialogTitle";
import divider from "@/themes/components/divider";
import flatpickr from "@/themes/components/flatpickr";
import autocomplete from "@/themes/components/form/autocomplete";
import checkbox from "@/themes/components/form/checkbox";
import formControlLabel from "@/themes/components/form/formControlLabel";
import formLabel from "@/themes/components/form/formLabel";
import input from "@/themes/components/form/input";
import inputLabel from "@/themes/components/form/inputLabel";
import inputOutlined from "@/themes/components/form/inputOutlined";
import radio from "@/themes/components/form/radio";
import select from "@/themes/components/form/select";
import switchButton from "@/themes/components/form/switchButton";
import textField from "@/themes/components/form/textField";
import icon from "@/themes/components/icon";
import iconButton from "@/themes/components/iconButton";
import linearProgress from "@/themes/components/linearProgress";
import link from "@/themes/components/link";
import list from "@/themes/components/list";
import listItem from "@/themes/components/list/listItem";
import listItemText from "@/themes/components/list/listItemText";
import menu from "@/themes/components/menu";
import menuItem from "@/themes/components/menu/menuItem";
import pickers from "@/themes/components/pickers";
import popover from "@/themes/components/popover";
import sidenav from "@/themes/components/sidenav";
import slider from "@/themes/components/slider";
import stepper from "@/themes/components/stepper";
import step from "@/themes/components/stepper/step";
import stepConnector from "@/themes/components/stepper/stepConnector";
import stepIcon from "@/themes/components/stepper/stepIcon";
import stepLabel from "@/themes/components/stepper/stepLabel";
import svgIcon from "@/themes/components/svgIcon";
import tableCell from "@/themes/components/table/tableCell";
import tableContainer from "@/themes/components/table/tableContainer";
import tableHead from "@/themes/components/table/tableHead";
import tabs from "@/themes/components/tabs";
import tab from "@/themes/components/tabs/tab";
import tooltip from "@/themes/components/tooltip";
import boxShadow from "@/themes/functions/boxShadow";
import hexToRgb from "@/themes/functions/hexToRgb";
import linearGradient from "@/themes/functions/linearGradient";
import pxToRem from "@/themes/functions/pxToRem";
import rgba from "@/themes/functions/rgba";

export const defaultTheme: ThemeOptions = {
  fontColors: {
    title: "#344767",
    description: "#A3A7B9",
    secondaryText: "#7B809A",
    disabled: "#EBEBE4",
    light: "#FFFFFF",
    dark: "#7B809A",
  },

  margins: {
    quarck: 4,
    nano: 8,
    xxxxs: 12,
    xxxs: 16,
    xxs: 24,
    xs: 32,
    sm: 40,
    md: 48,
    lg: 56,
    xl: 64,
    xxl: 80,
    xxxl: 96,
    huge: 104,
    giant: 112,
  },
  roundness: {
    none: 0,
    sm: 8,
    md: 16,
    lg: 24,
    circular: "50%",
  },
  fontWeight: {
    regular: 400,
    medium: 600,
    bold: 700,
  },
  fontSize: {
    xxxs: "0.625rem",
    xxs: "0.75rem",
    xs: "0.8125rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "4xl": "2rem",
    "5xl": "2.25rem",
    "6xl": "3rem",
    "7xl": "4rem",
    "8xl": "4.5rem",
    "9xl": "6rem",
  },

  breakpoints: { ...breakpoints },

  palette: {
    ...colors,
  },

  boxShadows: { ...boxShadows },

  typography: {
    ...typography,
  },

  borders: { ...borders },

  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...flatpickr,
        ...container,
        body: {
          backgroundColor: "#f0f2f5",
          fontFamily: "var(--font-roboto)",
        },
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: { ...textField },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiPopover: { ...popover },
    ...pickers,
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions },
  },
};

export default defaultTheme;
